import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { seoServices } from '../../../../services/seoAPIServices';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Search, Loader2, Image as ImageIcon } from 'lucide-react'; 
import ImageUploaderComponent from '../../../../components/ImageUploader';
import { uploadFileToS3 } from '../../../../utility/S3Uploader';
import { extractS3Path } from '../../../../utility/stripS3Prefix';
import { CategorySearch } from '../../../../components/Admin/Category/CategorySearch'; 
import LocationSearch from '../../../../components/Admin/SEO/LocationSearch';
import GroupSearch from '../../../../components/Admin/SEO/GroupSearch';
import MediaLibraryModal from '../../../../components/MediaLibraryModal'; 
import { useAdminStore } from '../../../../store/adminStore';
import LocationTagSelector from '../../../../components/Admin/SEO/LocationTagSelector'; 
import SeoPageSearch from '../../../../components/Admin/SEO/SeoPageSearch';
import { InputWithSlug } from '../../../../components/ui/input-with-slug'; 

// --- CONSTANTS ---
const PAGE_TYPES = {
  BUYER: ['BUYER_HUB','BUYER_INDUSTRY_HUB', 'BUYER_GROUP_HUB', 'BUYER_PARENT', 'BUYER_CHILD_NATIONAL', 'BUYER_CHILD_LOCAL', 'BUYER_ARTICLE','ARTICLE_HUB','BUYER_ARTICLE_HUB'],
  SELLER: ['SELLER_HUB', 'SELLER_INDUSTRY_HUB', 'SELLER_PARENT', 'SELLER_CHILD_NATIONAL', 'SELLER_CHILD_LOCAL', 'SELLER_ARTICLE','SELLER_ARTICLE_HUB']
};

const PAGE_TYPE_DOC_MAP = {
    'BUYER_HUB': 'pageHub',
    'BUYER_INDUSTRY_HUB': 'pageHub',
    'BUYER_GROUP_HUB': 'pageHub',
    'BUYER_PARENT': 'pageService',
    'BUYER_CHILD_NATIONAL': 'pageService',
    'BUYER_CHILD_LOCAL': 'pageService',
    'BUYER_ARTICLE': 'article',
    'ARTICLE_HUB': 'pageHub',
    'BUYER_ARTICLE_HUB': 'pageHub',
    'SELLER_ARTICLE_HUB': 'pageHub',
    
    'SELLER_HUB': 'pageHub',
    'SELLER_INDUSTRY_HUB': 'pageHub', // <--- NEW
    'SELLER_PARENT': 'pageService', // <--- NEW
    'SELLER_CHILD_NATIONAL': 'pageService',
    'SELLER_CHILD_LOCAL': 'pageService', // <--- NEW
    'SELLER_ARTICLE': 'article'
};

// --- FIXED: Updated to match backend 'category.py' exactly ---
const INDUSTRIES = [
    "home_services", 
    "business_and_professional_services", 
    "events_and_hospitality_services", 
    "education_and_training_services",
    "personal_services", 
    "trade_services", 
    "general_services"
];

const toProperCase = (str) => str.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

const SeoPageForm = ({ open, onOpenChange, initialData, audience, onSuccess }) => {
  const locale = useAdminStore((state) => state.locale);
  
  // -- UI States --
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [keywordEnabled, setKeywordEnabled] = useState(false);
  const [articleContext, setArticleContext] = useState('NONE');

  // -- Sanity Search States --
  const [sanityDocs, setSanityDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [sanitySearch, setSanitySearch] = useState('');
  const [isSanityDropdownOpen, setIsSanityDropdownOpen] = useState(false);
  const [isArticleCatTypeParent, setArticleCatTypeParent] = useState(false);

  const { register, handleSubmit, control, watch, setValue, reset, getValues } = useForm({
      defaultValues: {
          is_live: false,
          page_type: '',
          sanity_doc_id: '',
          title: '', 
          slug_override: '',
          hero_image: '',
          hero_image_alt: '',
          keyword: '',
          target_industry: '',
          suggested_group_id: '',
          redirect_page_id: '',
          location_tag_ids: []
      }
  });
  
  const watchedPageType = watch('page_type');
  const watchedSanityId = watch('sanity_doc_id');
  
  // -- Logic Helpers --
  const isLocal = watchedPageType?.includes('LOCAL');
  const isGroup = watchedPageType?.includes('GROUP');
  const isIndustry = watchedPageType?.includes('INDUSTRY');
  const isParent = (watchedPageType === 'BUYER_PARENT' || watchedPageType === 'SELLER_PARENT') ? true : false;
  console.log("isParent:", isParent);
  console.log("watchedPageType:", watchedPageType);
  console.log("isArticleCatTypeParent:", isArticleCatTypeParent);
  const isArticle = watchedPageType === 'BUYER_ARTICLE' || watchedPageType === 'SELLER_ARTICLE';
  const isChild = watchedPageType?.includes('CHILD')

  // -- Visibility Logic --
  const showCategory = !isArticle || ['PARENT', 'CHILD', 'CHILD_LOCAL'].includes(articleContext);
  const showGroup = (!isArticle && isGroup) || (isArticle && articleContext === 'GROUP');
  const showLocation = (!isArticle && isLocal) || (isArticle && articleContext === 'CHILD_LOCAL');
  const showIndustry = (!isArticle && isIndustry) || (isArticle && articleContext === 'INDUSTRY');
  const showArticleLocationTags =  ['CHILD_LOCAL'].includes(articleContext);

  // -- Initialize Form --
  useEffect(() => {
    if (open) {
      if (initialData) {
        reset({
            ...initialData,
            title: initialData.title || '',
            target_industry: initialData.target_industry || '',
            slug_override: initialData.slug_override || initialData.slug || '',
            keyword: initialData.keyword || '',
            suggested_group_id: initialData.suggested_group_id || '',
            redirect_page_id: initialData.redirect_page_id || '',
            location_tag_ids: initialData.location_tag_ids || [], 
            
            category: initialData.category_id ? { id: initialData.category_id, name: initialData.category_name } : null,
            location: initialData.location_id ? { id: initialData.location_id, city: initialData.city_name } : null,
            group: initialData.category_group_id ? { id: initialData.category_group_id, name: initialData.group_name } : null,
            suggested_group: initialData.suggested_group_id ? { id: initialData.suggested_group_id, name: initialData.suggested_group_name } : null,
        });
        setKeywordEnabled(!!initialData.keyword);
        
        if(initialData.page_type.includes('ARTICLE')) {
             if(initialData.category_id && initialData.location_id) setArticleContext('CHILD_LOCAL');
             else if(initialData.category_id) { 
                if(initialData.page_type.includes('CHILD')) { 
                    setArticleContext('CHILD'); setArticleCatTypeParent(false);
                }
                else {setArticleContext('PARENT'); setArticleCatTypeParent(true);} ;
             }
             else if(initialData.category_group_id) setArticleContext('GROUP');
             else if(initialData.target_industry) setArticleContext('INDUSTRY');
        }

      } else {
        reset({ page_type: PAGE_TYPES[audience][0], is_live: false, audience: audience });
        setKeywordEnabled(false);
        setArticleContext('NONE');
      }
    }
  }, [open, initialData, audience, reset]);

  // --- Fetch Sanity Docs ---
  useEffect(() => {
    if (!watchedPageType || !open) return;
    const targetDocType = PAGE_TYPE_DOC_MAP[watchedPageType];
    
    if (!targetDocType) {
        setSanityDocs([]);
        return;
    }

    const fetchDocs = async () => {
        setLoadingDocs(true);
        const titleType = isChild ? "child" : isParent ? "parent" : isArticle ? 'article' : null;
        const language = 'en';
        const audienceType = audience;
        const countryCode = locale;
        console.log("countryCode:",locale);
        console.log("Audience:", audienceType);
        console.log("language:",language)
        try {
            const docs = await seoServices.getSanityDocs(targetDocType,titleType, audienceType, countryCode, language);
            setSanityDocs(docs);
        } catch (error) {
            console.error("Failed to load sanity docs", error);
        } finally {
            setLoadingDocs(false);
        }
    };

    fetchDocs();
  }, [watchedPageType, open]);

  // -- Filter Sanity Docs --
  const filteredDocs = useMemo(() => {
     if(!sanitySearch) return sanityDocs.slice(0, 10);
     return sanityDocs.filter(d => 
         (d.title?.toLowerCase() || '').includes(sanitySearch.toLowerCase()) || 
         (d.id && d.id.includes(sanitySearch))
     ).slice(0, 10);
  }, [sanityDocs, sanitySearch]);

  const selectedDoc = sanityDocs.find(d => d.id === watchedSanityId);

  // -- Handlers --
  const handleLibrarySelect = (url) => {
    setValue('hero_image', extractS3Path(url), { shouldDirty: true });
    setIsLibraryOpen(false);
  };

  const handleImageUpload = async (file) => {
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop();
    const fileNameBase = file.name.substring(0, file.name.lastIndexOf('.'));
    const sanitizedBase = fileNameBase.replace(/[^a-zA-Z0-9-_]/g, '-').toLowerCase();
    const newFileName = `${sanitizedBase}-${timestamp}.${fileExt}`;

    const renamedFile = new File([file], newFileName, { type: file.type });
    
    try {
        const url = await uploadFileToS3(renamedFile, "public/seo/hero");
        setValue('hero_image', url, { shouldDirty: true });
    } catch (e) { alert("Upload failed"); }
  };

  const onSubmit = async (data) => {
    const currentSlug = getValues('slug_override');
    console.log("slug overide:", currentSlug)
    if (isArticle) {
        if (!currentSlug || currentSlug.trim() === '') {
            alert("For Articles, a Title and Slug are mandatory.");
            return;
        }
    }
    try {
      const payload = {
        ...data,
        audience, 
        keyword: keywordEnabled ? data.keyword : null,
        slug: currentSlug,        
        // --- FIXED: Ensure IDs are null if hidden ---
        target_industry: (showIndustry && data.target_industry) ? data.target_industry : null,
        category_id: (showCategory && data.category?.id) ? data.category.id : null,
        location_id: (showLocation && data.location?.id) ? data.location.id : null,
        category_group_id: (showGroup && data.group?.id) ? data.group.id : null,
        
        suggested_group_id: data.suggested_group ? data.suggested_group.id : null,
      };

      // Cleanup
      delete payload.category; delete payload.location; delete payload.group; delete payload.suggested_group;

      if (initialData?.id) {
        console.log("Update payload",payload)
        await seoServices.updatePage(initialData.id, payload);
      } else {
        await seoServices.createPage(payload);
      }
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.detail || "Operation failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initialData ? `Edit Page (${initialData.id})` : 'Create New Page'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          
          {/* Row 1: Page Type & Sanity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
                <Label>Page Type</Label>
                {initialData ? (
                     <Input value={initialData.page_type} disabled className="bg-gray-100" />
                ) : (
                    <Controller
                        name="page_type"
                        control={control}
                        render={({ field }) => (
                            <Select value={field.value} onValueChange={field.onChange}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {PAGE_TYPES[audience].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        )}
                    />
                )}
            </div>
            
            <div className="relative">
                 <Label>
                     Sanity Document ID 
                     {watchedPageType && <span className="text-gray-400 font-normal ml-2">({PAGE_TYPE_DOC_MAP[watchedPageType] || '...'})</span>}
                 </Label>
                 <div className="relative">
                    <div 
                        className="flex items-center justify-between border rounded p-2 bg-white cursor-pointer h-10 text-sm"
                        onClick={() => setIsSanityDropdownOpen(!isSanityDropdownOpen)}
                    >
                        {selectedDoc ? (
                            <span className="truncate w-[90%] font-medium">{selectedDoc.title}</span>
                        ) : (
                            <span className="text-gray-500">{watchedSanityId || "Select Document..."}</span>
                        )}
                        <Search className="w-4 h-4 text-gray-400" />
                    </div>

                    {isSanityDropdownOpen && (
                        <div className="absolute z-50 w-full bg-white border rounded shadow-lg mt-1 p-2">
                            <div className="flex items-center border-b pb-2 mb-2">
                                <Search className="w-4 h-4 text-gray-400 mr-2"/>
                                <input 
                                    className="outline-none text-sm w-full"
                                    placeholder="Search title..."
                                    value={sanitySearch}
                                    onChange={(e) => setSanitySearch(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <ul className="max-h-40 overflow-auto space-y-1">
                                {loadingDocs ? (
                                    <li className="text-center py-2"><Loader2 className="w-4 h-4 animate-spin mx-auto"/></li>
                                ) : filteredDocs.map(doc => (
                                    <li 
                                        key={doc.id} 
                                        onClick={() => {
                                            setValue('sanity_doc_id', doc.id); 
                                            setIsSanityDropdownOpen(false);
                                        }}
                                        className="text-xs p-2 hover:bg-gray-100 cursor-pointer rounded flex justify-between group"
                                    >
                                        <span className="truncate max-w-[180px] font-medium text-gray-700">{doc.title || 'Untitled'}</span>
                                        <div className="flex items-center gap-2">
                                            <span className={`px-1 rounded text-[10px] ${doc.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                {doc.status}
                                            </span>
                                        </div>
                                    </li>
                                ))}
                                {!loadingDocs && filteredDocs.length === 0 && <li className="text-xs text-gray-400 p-2">No docs found</li>}
                            </ul>
                        </div>
                    )}
                 </div>
            </div>
          </div>

          {/* Row 2: ARTICLE CONFIGURATION */}
          {isArticle && (
             <div className="bg-purple-50 p-4 rounded border border-purple-100 space-y-4">
                <div className="flex justify-between items-center">
                    <p className="text-xs font-bold text-purple-800 uppercase">Article Configuration</p>
                    <div className="px-2 py-0.5 bg-purple-200 text-purple-800 text-[10px] rounded font-bold">BLOG MODE</div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label>Article Context (Target)</Label>
                        <select 
                            className="w-full p-2 border rounded bg-white text-sm focus:ring-2 focus:ring-purple-200"
                            value={articleContext}
                            onChange={(e) => {
                                setArticleContext(e.target.value);  
                                e.target.value === "PARENT"? setArticleCatTypeParent(true) : setArticleCatTypeParent(false)}}
                        >
                            <option value="NONE">-- Generic / No Target --</option>
                            <option value="INDUSTRY">Industry Specific</option>
                            <option value="PARENT">Parent Category</option>
                            <option value="GROUP">Group / Collection</option>
                            <option value="CHILD">Child Category (National)</option>
                            <option value="CHILD_LOCAL">Child Category (Local)</option>
                        </select>
                        <p className="text-[10px] text-gray-500 mt-1">Determines where this article appears.</p>
                    </div>

                    {showArticleLocationTags &&<div>
                        <Label>Link Building Tags</Label>
                        <Controller
                            name="location_tag_ids"
                            control={control}
                            render={({ field }) => (
                                <LocationTagSelector 
                                    selectedIds={field.value || []}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </div>}
                </div>
             </div>
          )}

          {/* Row 3: PAGE CONFIGURATION */}
          <div className="bg-blue-50 p-4 rounded border border-blue-100 space-y-3">
               <div className="grid grid-cols-2 gap-4">
                   <div>
                      <Label>Suggested Group (Optional)</Label>
                      <Controller
                          name="suggested_group"
                          control={control}
                          render={({ field }) => (
                              <GroupSearch 
                                  countryCode={locale} 
                                  onSelect={field.onChange}
                                  placeholder={initialData?.suggested_group_name || "Select Group..."}
                                  purpose="SEO_PAGE" 
                              />
                          )}
                      />
                  </div>
                  <div>
                    <Label>Redirect To Page</Label>
                    <Controller
                        name="redirect_page_id" 
                        control={control}
                        render={({ field }) => (
                            <SeoPageSearch 
                                onSelect={field.onChange}
                                initialValue={initialData?.redirect_target_slug}
                                placeholder="Search target page..."
                            />
                        )}
                    />
                  </div>
               </div>
          </div>

          {/* Row 4: RELATIONSHIPS */}
          <div className="bg-gray-50 p-4 rounded border space-y-4">
             <p className="text-xs font-bold text-gray-400 uppercase">Page Context</p>
             
             {showIndustry && (
                 <div>
                    <Label>Target Industry</Label>
                    {/* FIXED: Values now match backend Enums */}
                    <select className="w-full p-2 border rounded bg-white text-sm" {...register('target_industry')}>
                            <option value="">-- Select Industry --</option>
                            {INDUSTRIES.map(ind => (
                                <option key={ind} value={ind}>{toProperCase(ind)}</option>
                            ))}
                    </select>
                 </div>
             )}

             {showGroup && (
                <div>
                    <Label>Category Group</Label>
                    <Controller
                        name="group"
                        control={control}
                        render={({ field }) => (
                            <GroupSearch 
                                countryCode={locale} 
                                onSelect={field.onChange}
                                placeholder={initialData?.group_name || "Select Group..."}
                            />
                        )}
                    />
                </div>
             )}

             <div className="grid grid-cols-2 gap-4">
                 {showCategory && !isIndustry && (
                    <div>
                        <Label>Category</Label>
                        <Controller
                            name="category"
                            control={control}
                            render={({ field }) => (
                                <CategorySearch 
                                    locale={locale} 
                                    onSelect={field.onChange}
                                    typeFilter={(isParent || isArticleCatTypeParent) ? 'parent' : 'child'} 
                                    placeholder={initialData?.category_name || "Search Category..."}
                                />
                            )}
                        />
                    </div>
                 )}

                 {showLocation && (
                    <div>
                        <Label>Location</Label>
                        <Controller
                            name="location"
                            control={control}
                            render={({ field }) => <LocationSearch countryCode={locale} onSelect={field.onChange} placeholder={initialData?.city_name || "Search..."}/>}
                        />
                    </div>
                 )}
             </div>
             
             {!showIndustry && !showGroup && !showCategory && !showLocation && (
                 <p className="text-sm text-gray-400 italic text-center py-2">No specific relationships required for this context.</p>
             )}
          </div>

          {/* Row 5: Metadata & Slug */}
          <div className="grid grid-cols-2 gap-4">
             <div className="border p-3 rounded">
                <div className="flex items-center gap-2 mb-2">
                    <input type="checkbox" checked={keywordEnabled} onChange={(e) => setKeywordEnabled(e.target.checked)} />
                    <Label>Target Niche Keyword?</Label>
                </div>
                {keywordEnabled && <Input {...register('keyword')} placeholder="e.g. 'budget', 'luxury'" />}
             </div>

             <div className={isArticle ? "col-span-1" : ""}>
                {isArticle ? (
                    <InputWithSlug 
                        // FIX: Add unique key to force re-mount when editing different pages
                        key={initialData?.id || 'new-article-slug'}
                        
                        titleLabel="Article Title"
                        slugLabel="URL Slug (Mandatory)"
                        
                        // FIX: Source initial values directly from data to prevent state loops
                        initialTitle={initialData?.title || getValues('title') || ''}
                        initialSlug={initialData?.slug_override || initialData?.slug || getValues('slug_override') || ''}
                        
                        // FIX: Ensure validation triggers on change
                        onTitleChange={(val) => setValue('title', val, { shouldValidate: true, shouldDirty: true })}
                        onSlugChange={(val) => setValue('slug_override', val, { shouldValidate: true, shouldDirty: true })}
                    />
                ) : (
                    <div>
                        <Label>Slug Override (Optional)</Label>
                        <Input {...register('slug_override')} placeholder="/custom-url-path" />
                    </div>
                )}
             </div>
          </div>
          
          {/* Row 6: Images & Live */}
          <div className="grid grid-cols-2 gap-4">
             <div>
                 <Label>Hero Image</Label>
                 <div className="flex gap-2 mt-1">
                    <ImageUploaderComponent 
                        onChange={handleImageUpload}
                        maxFiles={1}
                        initialUrls={watch('hero_image') ? [watch('hero_image')] : []}
                        onBrowse={() => setIsLibraryOpen(true)}
                    />
                 </div>
                 <Input {...register('hero_image_alt')} placeholder="Alt text" className="mt-2 text-xs" />
             </div>
             <div className="flex items-center gap-2 bg-gray-50 p-3 rounded h-fit self-end">
               <Controller 
                  name="is_live"
                  control={control}
                  render={({ field }) => (
                    <input 
                        type="checkbox" 
                        checked={field.value} 
                        onChange={field.onChange}
                        className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                    />
                  )}
               />
               <Label>Page is Live</Label>
             </div>
          </div>

          <DialogFooter>
             <Button type="submit">Save Changes</Button>
          </DialogFooter>
        </form>

        <MediaLibraryModal 
            open={isLibraryOpen} 
            onOpenChange={setIsLibraryOpen} 
            onSelect={handleLibrarySelect} 
        />
      </DialogContent>
    </Dialog>
  );
};

export default SeoPageForm;